import apiService from './apiService';

class TripService {
  async getTrips(skip = 0, limit = 10, tripNumber = '', createdDate = '', vehicleNumber = '', driverMobileNumber = '') {
    try {
      let endpoint = `/api/v1/trips/trips/?skip=${skip}&limit=${limit}`;

      if (tripNumber) {
        endpoint += `&trip_number=${encodeURIComponent(tripNumber)}`;
      }

      if (createdDate) {
        endpoint += `&created_date=${encodeURIComponent(createdDate)}`;
      }

      if (vehicleNumber) {
        endpoint += `&vehicle_number=${encodeURIComponent(vehicleNumber)}`;
      }

      if (driverMobileNumber) {
        endpoint += `&driver_mobile_number=${encodeURIComponent(driverMobileNumber)}`;
      }

      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getAllTrips() {
    try {

      const endpoint = '/api/v1/trips/trips/';
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getUnclosedTrips(skip = 0, limit = 100, tripNumber = '', vehicleNumber = '') {
    try {
      let endpoint = `/api/v1/trips/trips/unclosed?skip=${skip}&limit=${limit}`;
      if (tripNumber) {
        endpoint += `&trip_number=${encodeURIComponent(tripNumber)}`;
      }
      if (vehicleNumber) {
        endpoint += `&vehicle_number=${encodeURIComponent(vehicleNumber)}`;
      }
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to fetch unclosed trips:', error);
      throw error;
    }
  }

  async getActiveTrips(transporterId = null, tripNumber = '', vehicleNumber = '', skip = 0, limit = 20) {
    try {
      let endpoint;

      if (transporterId) {
        endpoint = `/api/v1/trips/trips/transporter/${transporterId}/active`;
        const params = [`skip=${skip}`, `limit=${limit}`];
        if (tripNumber) params.push(`trip_number=${encodeURIComponent(tripNumber)}`);
        if (vehicleNumber) params.push(`vehicle_number=${encodeURIComponent(vehicleNumber)}`);
        if (params.length > 0) {
          endpoint += `?${params.join('&')}`;
        }
      } else {
        endpoint = '/api/v1/trips/trips/active/';
        const params = [`skip=${skip}`, `limit=${limit}`];
        if (tripNumber) params.push(`trip_number=${encodeURIComponent(tripNumber)}`);
        if (vehicleNumber) params.push(`vehicle_number=${encodeURIComponent(vehicleNumber)}`);
        if (params.length > 0) {
          endpoint += `?${params.join('&')}`;
        }
      }

      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getTripById(id) {
    try {
      const endpoint = `/api/v1/trips/trips/${id}`;
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getTripOtps(id) {
    try {
      const endpoint = `/api/v1/trips/trips/${id}/otps`;
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async createTrip(tripData) {
    try {
      const endpoint = '/api/v1/trips/trips/';
      const response = await apiService.post(endpoint, this.normalizeTripMovement(tripData));
      return response;
    } catch (error) {
      console.error('Failed to create trip:', error);
      throw error;
    }
  }

  async updateTrip(id, tripData) {
    try {
      const endpoint = `/api/v1/trips/trips/${id}`;
      const response = await apiService.put(endpoint, this.normalizeTripMovement(tripData));
      return response;
    } catch (error) {
      console.error(`Failed to update trip with ID ${id}:`, error);
      throw error;
    }
  }

  normalizeTripMovement(tripData = {}) {
    const payload = { ...tripData };

    const rawScope = payload.movement;
    if (rawScope) {
      payload.movement = String(rawScope).toLowerCase() === 'secondary' ? 'Secondary' : 'Primary';
    } else if (payload.port_id) {
      payload.movement = 'Primary';
    } else {
      payload.movement = 'Secondary';
    }

    const rawMode = payload.movementType || payload.movement_type;
    payload.movementType = rawMode && String(rawMode).toUpperCase() === 'RAKE' ? 'RAKE' : 'ROAD';
    delete payload.movement_type;

    return payload;
  }

  async deleteTrip(id) {
    try {
      const endpoint = `/api/v1/trips/trips/${id}`;
      const response = await apiService.delete(endpoint);
      return response;
    } catch (error) {
      console.error(`Failed to delete trip with ID ${id}:`, error);
      throw error;
    }
  }

  async changeDriver(tripId, driverData) {
    try {
      const endpoint = `/api/v1/trips/trips/${tripId}/change-driver-vehicle`;
      const response = await apiService.post(endpoint, driverData);
      return response;
    } catch (error) {
      console.error(`Failed to change driver for trip with ID ${tripId}:`, error);
      throw error;
    }
  }

  async uploadTripImage(entityId, file, fileType, description = '') {
    try {
      if (!file || !(file instanceof File)) {
        throw new Error('Invalid file object provided for upload');
      }

      const endpoint = `/api/v1/enhanced-files/upload/trip/${entityId}`;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'trip_photo');
      formData.append('description', description || `Trip photo for trip ${entityId}`);

      const response = await apiService.uploadFile(endpoint, formData);
      return response;
    } catch (error) {
      console.error(`Failed to upload image for trip ${entityId}:`, error);
      if (error.message) {
        throw new Error(`File upload failed: ${error.message}`);
      } else {
        throw new Error('File upload failed due to an unknown error. Please check the console for more details.');
      }
    }
  }

  async uploadTripItemFile(tripItemId, file, fileType, description = '') {
    try {
      if (!file || !(file instanceof File)) {
        throw new Error('Invalid file object provided for upload');
      }

      const endpoint = `/api/v1/enhanced-files/upload/trip_item/${tripItemId}`;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      formData.append('description', description || `File for trip item ${tripItemId}`);

      const response = await apiService.uploadFile(endpoint, formData);
      return response;
    } catch (error) {
      console.error(`Failed to upload file for trip item ${tripItemId}:`, error);
      if (error.message) {
        throw new Error(`File upload failed: ${error.message}`);
      } else {
        throw new Error('File upload failed due to an unknown error. Please check the console for more details.');
      }
    }
  }

  async getDashboardStatistics() {
    try {
      const endpoint = '/api/v1/trips/trips/dashboard/statistics';
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getDashboardOverview() {
    try {
      const endpoint = '/api/v1/dashboard/overview';
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      throw error;
    }
  }


  async raiseTicket(tripId, ticketData) {
    try {
      const endpoint = `/api/v1/trips/trips/${tripId}/tickets`;

      const response = await apiService.post(endpoint, ticketData);
      return response;
    } catch (error) {
      console.error(`Failed to raise ticket for trip with ID ${tripId}:`, error);
      throw error;
    }
  }

  async getRakeReceipts(skip = 0, limit = 100, receiptStatus = 'pending', consigneeState = '', rrNumber = '', tripNumber = '') {
    try {
      let endpoint = `/api/v1/trips/rake-receipts?skip=${skip}&limit=${limit}&receipt_status=${receiptStatus}`;
      if (consigneeState) {
        endpoint += `&consignee_state=${encodeURIComponent(consigneeState)}`;
      }
      if (rrNumber) {
        endpoint += `&rr_number=${encodeURIComponent(rrNumber)}`;
      }
      if (tripNumber) {
        endpoint += `&trip_number=${encodeURIComponent(tripNumber)}`;
      }
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to fetch rake receipts:', error);
      throw error;
    }
  }

  async receiveRakeTrip(tripId, receivedQtyMt, receivedLocationRakePointId, quantityDetails, receivingRegion = '', receivedBy = '') {
    try {
      const endpoint = `/api/v1/trips/trips/${tripId}/receive`;
      const payload = {
        received_qty_mt: parseFloat(receivedQtyMt),
        received_location_rake_point_id: receivedLocationRakePointId,
        quantity_details: quantityDetails,
      };
      if (receivingRegion) {
        payload.receiving_region = receivingRegion;
      }
      if (receivedBy) {
        payload.received_by = receivedBy;
      }
      const response = await apiService.post(endpoint, payload);
      return response;
    } catch (error) {
      console.error(`Failed to record receipt for trip with ID ${tripId}:`, error);
      throw error;
    }
  }

  /**
   * Log Gate-In Receipt for Rake Trip
   */
  async rakeGateIn(tripId, gateInData) {
    try {
      const endpoint = `/api/v1/trips/trips/${tripId}/receive`;
      const response = await apiService.post(endpoint, gateInData);
      return response;
    } catch (error) {
      console.error(`Failed to complete gate-in for trip with ID ${tripId}:`, error);
      throw error;
    }
  }


  async getDispatchBalance(tripId) {
    try {
      const endpoint = `/api/v1/trips/trips/${tripId}/dispatch-balance`;
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      console.error(`Failed to fetch dispatch balance for trip with ID ${tripId}:`, error);
      throw error;
    }
  }

  async createEditRequest(tripId, requestReason, changes) {
    try {
      const endpoint = `/api/v1/trips/${tripId}/edit-requests`;
      const payload = {
        request_reason: requestReason,
        changes: changes,
      };
      const response = await apiService.post(endpoint, payload);
      return response;
    } catch (error) {
      console.error(`Failed to create edit request for trip with ID ${tripId}:`, error);
      throw error;
    }
  }

  async getEditRequests(status = '', mine = false, skip = 0, limit = 100) {
    try {
      let endpoint = `/api/v1/trips/edit-requests?skip=${skip}&limit=${limit}`;
      if (status) {
        endpoint += `&status=${status}`;
      }
      if (mine) {
        endpoint += `&mine=true`;
      }
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      console.error('Failed to fetch edit requests:', error);
      throw error;
    }
  }

  async getEditRequestById(requestId) {
    try {
      const endpoint = `/api/v1/trips/edit-requests/${requestId}`;
      const response = await apiService.get(endpoint);
      return response;
    } catch (error) {
      console.error(`Failed to fetch edit request with ID ${requestId}:`, error);
      throw error;
    }
  }

  async applyEditRequest(requestId) {
    try {
      const endpoint = `/api/v1/trips/edit-requests/${requestId}/apply`;
      const response = await apiService.post(endpoint, {});
      return response;
    } catch (error) {
      console.error(`Failed to apply edit request with ID ${requestId}:`, error);
      throw error;
    }
  }

  async rejectEditRequest(requestId, rejectionReason) {
    try {
      const endpoint = `/api/v1/trips/edit-requests/${requestId}/reject`;
      const payload = {
        rejection_reason: rejectionReason,
      };
      const response = await apiService.post(endpoint, payload);
      return response;
    } catch (error) {
      console.error(`Failed to reject edit request with ID ${requestId}:`, error);
      throw error;
    }
  }

  async verifyEditRequest(requestId, verificationNotes = '') {
    try {
      const endpoint = `/api/v1/trips/edit-requests/${requestId}/verify`;
      const payload = {};
      if (verificationNotes) {
        payload.verification_notes = verificationNotes;
      }
      const response = await apiService.post(endpoint, payload);
      return response;
    } catch (error) {
      console.error(`Failed to verify edit request with ID ${requestId}:`, error);
      throw error;
    }
  }

  async notVerifyEditRequest(requestId, verificationNotes) {
    try {
      const endpoint = `/api/v1/trips/edit-requests/${requestId}/not-verify`;
      const payload = {
        verification_notes: verificationNotes,
      };
      const response = await apiService.post(endpoint, payload);
      return response;
    } catch (error) {
      console.error(`Failed to not-verify edit request with ID ${requestId}:`, error);
      throw error;
    }
  }

  async cancelEditRequest(requestId) {
    try {
      const endpoint = `/api/v1/trips/edit-requests/${requestId}`;
      const response = await apiService.delete(endpoint);
      return response;
    } catch (error) {
      console.error(`Failed to cancel edit request with ID ${requestId}:`, error);
      throw error;
    }
  }
}

export default new TripService();